// src/app/core/services/date-format.service.ts
import { Injectable, NgZone } from '@angular/core';
import { formatDate } from '@angular/common';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { UserSettingsService } from './user-settings.service';
import { UserSettingStore } from './user-setting-store.base';
import {
  coerceDateFormat, coerceFirstDayOfWeek, coerceTimeFormat, DateFormatPref, DateKind,
  DATE_FORMAT_KEY, FIRST_DAY_OF_WEEK_KEY, FirstDayOfWeekPref, resolveFormat, TimeFormatPref, TIME_FORMAT_KEY,
} from './date-format.util';

const DEFAULT_LOCALE = 'it';

/**
 * App-wide cache of the user's date/time display preferences. A global pipe can't
 * issue an HTTP request per render, so the keys live here in BehaviorSubjects, loaded
 * once per authenticated transition and mutated optimistically. The cross-tab channel,
 * auth-gated load, and optimistic write live in UserSettingStore (shared with
 * NumberFormatService).
 */
@Injectable({ providedIn: 'root' })
export class DateFormatService extends UserSettingStore {
  private readonly dateFormat = new BehaviorSubject<DateFormatPref>('system');
  private readonly timeFormat = new BehaviorSubject<TimeFormatPref>('system');
  private readonly firstDayOfWeek = new BehaviorSubject<FirstDayOfWeekPref>('sunday');

  readonly dateFormat$: Observable<DateFormatPref> = this.dateFormat.asObservable();
  readonly timeFormat$: Observable<TimeFormatPref> = this.timeFormat.asObservable();
  readonly firstDayOfWeek$: Observable<FirstDayOfWeekPref> = this.firstDayOfWeek.asObservable();

  /** Current date-format preference, read synchronously. Used by the Material
   *  DateAdapter, whose `format()` is synchronous and runs on every picker render. */
  get currentDateFormat(): DateFormatPref {
    return this.dateFormat.value;
  }

  /** Current first day of week as the Material day index (Sunday 0, Monday 1).
   *  Read synchronously by the DateAdapters' getFirstDayOfWeek(). */
  get currentFirstDayOfWeek(): number {
    return this.firstDayOfWeek.value === 'monday' ? 1 : 0;
  }

  constructor(userSettings: UserSettingsService, store: Store, zone: NgZone) {
    super(userSettings, store, zone);
  }

  formatDate(value: unknown, fallback = 'mediumDate', kind?: DateKind, locale: string = DEFAULT_LOCALE): string | null {
    if (value == null || value === '') return null;
    const fmt = resolveFormat(fallback, this.dateFormat.value, this.timeFormat.value, kind);
    try {
      return formatDate(value as string | number | Date, fmt, locale);
    } catch {
      return null;
    }
  }

  setDateFormat(value: DateFormatPref): void {
    this.setPref(this.dateFormat, DATE_FORMAT_KEY, value, 'system');
  }

  setTimeFormat(value: TimeFormatPref): void {
    this.setPref(this.timeFormat, TIME_FORMAT_KEY, value, 'system');
  }

  setFirstDayOfWeek(value: FirstDayOfWeekPref): void {
    this.setPref(this.firstDayOfWeek, FIRST_DAY_OF_WEEK_KEY, value, 'sunday');
  }

  protected load(userId: number): void {
    // Use the STRICT shared load (keyed by userId) so a final failure — after its internal retries —
    // reaches the error handler instead of arriving as an indistinguishable `{}`. The base sets the
    // per-identity guard optimistically to dedupe concurrent readiness emissions; if the load ultimately
    // FAILS we re-arm it (rearmLoad) so the next /app re-entry retries — otherwise a transient
    // /v2/userSettings outage in the post-login window would pin the whole session on default ('system')
    // date/time formats until a full logout/login. Shares the same single GET as ReleaseNotesStateService.
    this.userSettings
      .getAllSharedStrict(userId)
      .pipe(take(1))
      .subscribe({
        next: settings => {
          // Route through emit() (not a bare .next()): with the common default prefs the loaded value
          // equals what the subject already holds, so an unconditional .next() would fire a no-op
          // app-wide CD pass on every login. Matches onBroadcast().
          this.emit(this.dateFormat, coerceDateFormat(settings[DATE_FORMAT_KEY]));
          this.emit(this.timeFormat, coerceTimeFormat(settings[TIME_FORMAT_KEY]));
          this.emit(this.firstDayOfWeek, coerceFirstDayOfWeek(settings[FIRST_DAY_OF_WEEK_KEY]));
        },
        error: () => this.rearmLoad(userId),
      });
  }

  protected override handlesKey(key?: string): boolean {
    return key === DATE_FORMAT_KEY || key === TIME_FORMAT_KEY || key === FIRST_DAY_OF_WEEK_KEY;
  }

  protected onBroadcast(data: { key?: string; value?: unknown }): void {
    if (!data?.key) return;
    if (data.key === DATE_FORMAT_KEY) this.emit(this.dateFormat, coerceDateFormat(data.value));
    else if (data.key === TIME_FORMAT_KEY) this.emit(this.timeFormat, coerceTimeFormat(data.value));
    else if (data.key === FIRST_DAY_OF_WEEK_KEY) this.emit(this.firstDayOfWeek, coerceFirstDayOfWeek(data.value));
  }
}
